import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Select,
  Input,
  Tag,
  Tooltip,
  Typography,
  Row,
  Col,
  Card,
  Badge,
  Modal,
  Space,
  Divider,
  Pagination,
} from 'antd';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import Icons from '@/components/third-party/Icons';
import { defaultColors } from '@/themes/antd-theme';
import { useTheme } from '@/App';
import { get_outbound_events } from '@/utils/api';
import StatCard from '@/components/StatCard';
import ReusableSearchInput from '@/components/ReusableSearch';
import useTableConfig from '@/hooks/useTableConfig';
import TableColumnConfig from '@/components/TableColumnConfig';
import { Workflow, Radio, CheckCircle, Clock, AlertCircle } from 'lucide-react';

const { Title, Text } = Typography;

const EVENT_CATEGORIES = [
  { label: 'All Categories', value: '' },
  { label: 'Config Event', value: 'config_event' },
  { label: 'Batch Event', value: 'batch_event' },
];

const PUBLISHED_OPTIONS = [
  { label: 'All Status', value: '' },
  { label: 'Published', value: 'true' },
  { label: 'Unpublished', value: 'false' },
];

export default function EventLogs() {
  const { isDark } = useTheme();
  const textColor = isDark ? '#ffffff' : '#1f2937';

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const [eventCategory, setEventCategory] = useState('');
  const [eventType, setEventType] = useState('');
  const [published, setPublished] = useState('');
  const [searchvalue, setSearchvalue] = useState('');

  const [payloadModal, setPayloadModal] = useState({ open: false, payload: null, eventType: '' });

  const fetchEvents = useCallback(() => {
    setLoading(true);
    const params = { page_number: pageNumber, page_size: pageSize };
    if (eventCategory) params.event_category = eventCategory;
    if (eventType) params.event_type = eventType;
    if (published !== '') params.published = published;
    if (searchvalue) params.search = searchvalue;

    get_outbound_events((res) => {
      setLoading(false);
      if (res?.data?.status === 'success') {
        setData(res.data.data || []);
        setTotalCount(res.data.total_count || 0);
      } else {
        toast.error(res?.data?.message || 'Failed to fetch event logs');
      }
    }, params);
  }, [pageNumber, pageSize, eventCategory, eventType, published, searchvalue]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const publishedCount = data.filter((d) => d.published).length;
  const pendingCount = data.filter((d) => !d.published).length;
  const errorCount = data.filter((d) => d.last_error).length;

  const allColumns = [
    {
      key: 'event_type',
      title: 'Event Type',
      dataIndex: 'event_type',
      defaultVisible: true,
      alwaysVisible: false,
      sortable: true,
      sortType: 'string',
      align: 'left',
      render: (val) => (
        <Text strong style={{ color: textColor, fontSize: 12 }}>
          {val}
        </Text>
      ),
    },
    {
      key: 'event_category',
      title: 'Category',
      dataIndex: 'event_category',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      align: 'left',
      render: (val) =>
        val ? (
          <Tag color={val === 'config_event' ? 'blue' : 'purple'} style={{ fontSize: 11 }}>
            {val}
          </Tag>
        ) : (
          <span style={{ color: '#9ca3af' }}>—</span>
        ),
    },
    {
      key: 'published',
      title: 'Status',
      dataIndex: 'published',
      defaultVisible: true,
      sortable: false,
      align: 'center',
      render: (val) =>
        val ? (
          <Badge status="success" text={<span style={{ fontSize: 12, color: '#16a34a' }}>Published</span>} />
        ) : (
          <Badge status="default" text={<span style={{ fontSize: 12, color: '#6b7280' }}>Pending</span>} />
        ),
    },
    {
      key: 'published_at',
      title: 'Published At',
      dataIndex: 'published_at',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      align: 'left',
      render: (val) =>
        val ? (
          <Text style={{ fontSize: 12, color: isDark ? '#9ca3af' : '#374151' }}>
            {dayjs(val).format('DD MMM YYYY HH:mm:ss')}
          </Text>
        ) : (
          <span style={{ color: '#9ca3af' }}>—</span>
        ),
    },
    {
      key: 'created_at',
      title: 'Created At',
      dataIndex: 'created_at',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      align: 'left',
      render: (val) => (
        <Text style={{ fontSize: 12, color: isDark ? '#9ca3af' : '#374151' }}>
          {dayjs(val).format('DD MMM YYYY HH:mm:ss')}
        </Text>
      ),
    },
    {
      key: 'request_id',
      title: 'Request ID',
      dataIndex: 'request_id',
      defaultVisible: true,
      sortable: false,
      align: 'left',
      render: (val) =>
        val ? (
          <Text copyable style={{ fontSize: 11, color: isDark ? '#93c5fd' : '#1d4ed8' }}>
            {val}
          </Text>
        ) : (
          <span style={{ color: '#9ca3af' }}>—</span>
        ),
    },
    {
      key: 'last_error',
      title: 'Last Error',
      dataIndex: 'last_error',
      defaultVisible: true,
      sortable: false,
      align: 'left',
      render: (val) =>
        val ? (
          <Tooltip title={val}>
            <Tag color="red" style={{ fontSize: 11, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>
              {val.length > 22 ? val.slice(0, 22) + '…' : val}
            </Tag>
          </Tooltip>
        ) : (
          <span style={{ color: '#9ca3af' }}>—</span>
        ),
    },
    {
      key: 'payload',
      title: 'Payload',
      dataIndex: 'payload',
      defaultVisible: true,
      alwaysVisible: true,
      sortable: false,
      align: 'center',
      width: 80,
      render: (payload, record) => (
        <Tooltip title="View Payload">
          <Icons.Eye
            className="w-5 h-5 cursor-pointer hover:scale-110 transition-transform"
            style={{ color: '#3b82f6' }}
            onClick={() => setPayloadModal({ open: true, payload, eventType: record.event_type })}
          />
        </Tooltip>
      ),
    },
  ];

  const { visibleColumns, columnConfigMenu, filteredData } = useTableConfig({
    tableKey: 'eventLogsTable',
    columns: allColumns,
    data: data,
    searchValue: searchvalue,
  });

  return (
    <Card
      className="m-2.5 border border-gray-300 shadow-md"
      title={
        <div className="flex items-center gap-2">
          <Icons.Workflow className="w-7 h-7 text-blue-600" />
          <Title level={4} style={{ color: textColor, margin: 0 }}>
            Event Logs
          </Title>
        </div>
      }
      extra={
        <div className="flex items-center gap-2">
          <Select
            style={{ width: 160 }}
            placeholder="Category"
            options={EVENT_CATEGORIES}
            value={eventCategory || undefined}
            allowClear
            onChange={(val) => { setEventCategory(val || ''); setPageNumber(1); }}
          />
          <Input
            style={{ width: 170 }}
            placeholder="Event Type..."
            value={eventType}
            allowClear
            onChange={(e) => { setEventType(e.target.value); setPageNumber(1); }}
          />
          <Select
            style={{ width: 140 }}
            placeholder="Status"
            options={PUBLISHED_OPTIONS}
            value={published !== '' ? published : undefined}
            allowClear
            onChange={(val) => { setPublished(val ?? ''); setPageNumber(1); }}
          />
          <TableColumnConfig menu={columnConfigMenu} />
          <ReusableSearchInput
            placeholder="Search here..."
            width={220}
            onSearch={(value) => { setSearchvalue(value); setPageNumber(1); }}
            onChange={(e) => { setSearchvalue(e.target.value); setPageNumber(1); }}
          />
        </div>
      }
    >
      {/* KPI Row */}
      <Row gutter={[12, 12]} className="mb-4">
        <Col xs={12} sm={6}>
          <StatCard
            icon={<Radio />}
            label="Total Events"
            value={totalCount}
            bgColor={isDark ? '#1e3a5f' : '#dbeafe'}
            iconColor="#3b82f6"
          />
        </Col>
        <Col xs={12} sm={6}>
          <StatCard
            icon={<CheckCircle />}
            label="Published"
            value={publishedCount}
            bgColor={isDark ? defaultColors.palette.green[900] : '#dcfce7'}
            iconColor="#16a34a"
          />
        </Col>
        <Col xs={12} sm={6}>
          <StatCard
            icon={<Clock />}
            label="Pending"
            value={pendingCount}
            bgColor={isDark ? defaultColors.palette.gray[700] : '#f3f4f6'}
            iconColor="#6b7280"
          />
        </Col>
        <Col xs={12} sm={6}>
          <StatCard
            icon={<AlertCircle />}
            label="With Errors"
            value={errorCount}
            bgColor={isDark ? defaultColors.palette.red[900] : '#fee2e2'}
            iconColor="#dc2626"
          />
        </Col>
      </Row>

      <Divider className="my-3" />

      <Table
        bordered
        size="small"
        dataSource={filteredData}
        columns={visibleColumns}
        rowKey="id"
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={false}
      />

      <div
        className="flex items-center justify-between mt-3 flex-wrap gap-2"
        style={{
          padding: '10px 0',
          borderTop: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
        }}
      >
        <Text style={{ fontSize: 12, color: isDark ? '#9ca3af' : '#6b7280' }}>
          Showing{' '}
          <span style={{ fontWeight: 600, color: textColor }}>
            {totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1}–{Math.min(pageNumber * pageSize, totalCount)}
          </span>{' '}
          of{' '}
          <span style={{ fontWeight: 600, color: textColor }}>{totalCount}</span> events
        </Text>
        <Pagination
          current={pageNumber}
          pageSize={pageSize}
          total={totalCount}
          showSizeChanger
          pageSizeOptions={['10', '15', '25', '50']}
          showQuickJumper
          onChange={(page, size) => {
            setPageNumber(page);
            setPageSize(size);
          }}
          onShowSizeChange={(current, size) => {
            setPageNumber(1);
            setPageSize(size);
          }}
        />
      </div>

      {/* Payload Modal */}
      <Modal
        open={payloadModal.open}
        title={
          <div className="flex items-center gap-2">
            <Icons.Eye className="w-4 h-4 text-blue-500" />
            <span style={{ color: textColor }}>
              Payload —{' '}
              <span style={{ color: '#3b82f6' }}>{payloadModal.eventType}</span>
            </span>
          </div>
        }
        onCancel={() => setPayloadModal({ open: false, payload: null, eventType: '' })}
        footer={null}
        width={560}
      >
        <pre
          style={{
            backgroundColor: isDark ? '#1e293b' : '#f8fafc',
            color: isDark ? '#e2e8f0' : '#1e293b',
            padding: 16,
            borderRadius: 8,
            fontSize: 13,
            overflow: 'auto',
            maxHeight: 420,
            border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
          }}
        >
          {JSON.stringify(payloadModal.payload, null, 2)}
        </pre>
      </Modal>
    </Card>
  );
}
